/**
 * 業務フローの図 1 枚（`/flows/$flowId`）。
 *
 * このルートは業務フロー 1 本ぶんだけを描く（1 図 ＝ 1 フロー）。支援フローは主フローの図に
 * 混ぜず、ステージの図の札から別の図へ飛ぶ。「誰が（レーン）どの順で（枝）何をするか（業務）」が
 * 1 画面に収まる。
 *
 * この画面は**マスターだけ**を描く。案件の差分は案件を主語にした画面（`PjFlowPage`）が
 * 持つ。図の中に案件番号のセレクタを置くと、案件が画面の一属性に見えるが、実際には案件は
 * 画面より上位の軸である。案件を先に選んでから図に入る導線に揃える。
 *
 * **読取は `getFlow`（`flowId`）である。** 節点は業務を直に指し、id は number である。
 *
 * **図と詳細の組み立ては `story-flow/` の部品に預ける。** 案件から見た画面（`PjFlowPage`）と
 * 同じ部品を通すので、「節点を選ぶと右に説明が出る」「押せば広げられる」が画面によって
 * 変わらない。この画面が持つのは、取得・URL の読み・選択の状態だけである。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フロー 1 本を、マスターの姿としてレーン帯の図と詳細ペインで描く。
 * @graph-connects useFlow [calls] 業務フロー 1 本の取得
 * @graph-connects StoryFlowView [calls] 図・詳細・全画面の描画
 */
import { Skeleton } from '@cortex/web-design-system/components/ui/Skeleton';
import { Link, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, NotFoundState } from '../components/spec/fallback-states.js';
import { useFlow } from '../hooks/queries/spec-graph-flow-queries.js';
import { isNotFoundError } from '../lib/orpc-errors.js';
import { BUSINESS_MAP_ROUTE, FLOW_ROUTE } from './route-paths.js';
import { readFlowIdParam } from './story-flow/story-flow-params.js';
import { StoryFlowView } from './story-flow/StoryFlowView.js';

/**
 * 業務フローの図 1 枚を、URL の誤り・取得中・未存在・失敗・本体の 5 状態で描く。
 *
 * @returns 業務フローの図
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローの図を、URL の誤り・取得中・未存在・失敗・本体の 5 状態で描く。
 * @graph-connects readFlowIdParam [calls] URL から業務フローの id を読む
 * @graph-connects FlowBody [calls] id が読めたあとの取得と描画
 */
export function FlowPage() {
  const { flowId } = useParams({ from: FLOW_ROUTE });
  const id = readFlowIdParam(flowId);
  return id === null ? <FlowNotFound id={flowId} /> : <FlowBody flowId={id} rawId={flowId} />;
}

/** `FlowBody` の props。 @graph-connects none */
interface FlowBodyProps {
  /** 読取へ渡す業務フローの id。 */
  flowId: number;
  /** URL に書かれていた綴り。未存在の案内に出す。 */
  rawId: string;
}

/**
 * 業務フローの id が読めたあとの取得と描画。
 *
 * **id の関門（`FlowPage`）から分けてある。** 関門で弾いた綴りは読取へ渡せない
 * 一方、フックは条件付きで呼べない。分ければ、読取を呼ぶ側は必ず正しい id を持っている。
 *
 * @param props 業務フローの id と URL の綴り
 * @returns 取得中・未存在・失敗・本体のいずれか
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローを取得し、取得中・未存在・失敗・本体の 4 状態で描く。
 * @graph-connects useFlow [calls] 業務フロー 1 本の取得
 * @graph-connects StoryFlowView [calls] 図・詳細・全画面の描画
 */
function FlowBody({ flowId, rawId }: FlowBodyProps) {
  const { t } = useTranslation();
  const flow = useFlow(flowId);
  /*
   * 選んだ節点はこの画面が持つ。もう一度押すと外す（右パネルが業務フローの説明に戻る）。
   */
  const [selectedNodeId, setSelectedNodeId] = useState<number | null>(null);

  if (isNotFoundError(flow.error)) return <FlowNotFound id={rawId} />;
  if (flow.error !== null && flow.error !== undefined) return <ErrorState error={flow.error} />;

  if (flow.data === undefined) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true" aria-label={t('common.loading')}>
        <Skeleton variant="rectangular" height="4rem" />
        <Skeleton variant="rectangular" height="24rem" />
      </div>
    );
  }

  return (
    <StoryFlowView
      flow={flow.data}
      selectedNodeId={selectedNodeId}
      onSelect={(nodeId) => {
        setSelectedNodeId((current) => (current === nodeId ? null : nodeId));
      }}
    />
  );
}

/** `FlowNotFound` の props。 @graph-connects none */
interface FlowNotFoundProps {
  /** URL に書かれていた綴り。 */
  id: string;
}

/**
 * 業務フローが見つからないときの案内。
 *
 * 戻り先を業務マップにするのは、業務フローがステージの下に並び、ステージとフローは業務マップから
 * 辿るためである。
 *
 * @param props URL に書かれていた綴り
 * @returns 未存在の案内
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローが見つからないことを、業務マップへの戻り口つきで案内する。
 * @graph-connects none
 */
function FlowNotFound({ id }: FlowNotFoundProps) {
  const { t } = useTranslation();
  return (
    <NotFoundState
      id={id}
      action={
        <Link
          to={BUSINESS_MAP_ROUTE}
          className="text-sm text-[var(--color-accent-primary)] underline"
        >
          {t('spec.backToList')}
        </Link>
      }
    />
  );
}
